import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Building2, ChevronDown, Check, Settings } from 'lucide-react';
import { toast } from 'sonner';
import companyService, { Company } from '@/services/companyService';
import authService from '@/services/authService';

interface CompanySwitcherProps {
  onCompanyChange?: (company: Company) => void;
  showManagementButton?: boolean;
  className?: string;
}

const CompanySwitcher: React.FC<CompanySwitcherProps> = ({ 
  onCompanyChange,
  showManagementButton = false,
  className = ""
}) => {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    (async () => {
      await waitForAuthReady();
      loadCurrentCompany();
      loadAvailableCompanies();
    })();
  }, []);

  const waitForAuthReady = async () => {
    // Wait for authService to populate after hard refresh
    let retries = 0;
    const maxRetries = 80; // up to ~8s for hard refresh cases
    while (retries < maxRetries) {
      const user = authService.getCurrentUser();
      const profile = authService.getUserProfile();
      if (user && profile) return;
      await new Promise((r) => setTimeout(r, 100));
      retries++;
    }
  };

  const loadCurrentCompany = async () => {
    try {
      const company = await companyService.getCurrentCompany();
      setCurrentCompany(company);
    } catch (error) {
      console.error('Error loading current company:', error);
    }
  };

  const loadAvailableCompanies = async () => {
    try {
      const companies = await companyService.getUserCompanies();
      setAvailableCompanies(companies);
    } catch (error: any) {
      console.error('Error loading available companies:', error);
      const msg = String(error?.message || '');
      if (msg.includes('用戶未登入')) {
        // transient on F5 before auth hydrates; retry a few times
        setTimeout(loadAvailableCompanies, 500);
        return;
      }
      toast.error('載入公司列表失敗: ' + (error.message || '未知錯誤'));
    }
  };

  const handleCompanySwitch = async (company: Company) => {
    if (company.id === currentCompany?.id) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      await companyService.switchCompany(company.id);
      setCurrentCompany(company);
      toast.success(`已切換到 ${company.name}`);
      
      if (onCompanyChange) {
        onCompanyChange(company);
      }
      
      setIsOpen(false);

      // Reload the page to refresh all company-specific data
      window.location.reload();
    } catch (error: any) {
      toast.error('切換公司失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserRoleInCompany = async (companyId: string): Promise<string> => {
    try {
      const role = await companyService.getUserRoleInCompany(companyId);
      return role || 'user';
    } catch (error) {
      return 'user';
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge variant="destructive" className="text-xs">管理員</Badge>;
      case 'manager':
        return <Badge className="bg-blue-500 text-xs">經理</Badge>;
      case 'user':
        return <Badge variant="secondary" className="text-xs">用戶</Badge>;
      case 'viewer':
        return <Badge variant="outline" className="text-xs">查看者</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{role}</Badge>;
    }
  };

  if (!currentCompany && availableCompanies.length === 0) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50 ${className}`}
          disabled={loading}
        >
          <div className="flex items-center gap-2 min-w-0">
            {currentCompany?.logo_url ? (
              <img
                src={currentCompany.logo_url}
                alt={currentCompany.name}
                className="w-5 h-5 rounded object-cover flex-shrink-0"
              />
            ) : (
              <Building2 className="w-4 h-4 flex-shrink-0" />
            )}
            <span className="truncate max-w-32">
              {currentCompany?.name || '選擇公司'}
            </span>
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-80 bg-slate-800 border-slate-700 text-white"
        align="end"
      >
        <DropdownMenuLabel className="text-slate-400">
          切換公司
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-700" />
        
        {availableCompanies.length === 0 ? (
          <DropdownMenuItem disabled className="text-slate-400">
            沒有可用的公司
          </DropdownMenuItem>
        ) : (
          availableCompanies.map((company) => (
            <DropdownMenuItem
              key={company.id}
              onClick={() => handleCompanySwitch(company)}
              className="flex items-center justify-between py-3 px-3 hover:bg-slate-700 cursor-pointer"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={company.name}
                    className="w-8 h-8 rounded object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-8 h-8 bg-slate-700 rounded flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-slate-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white truncate">
                      {company.name}
                    </span>
                    {company.id === currentCompany?.id && (
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                    )}
                  </div>
                  {company.code && (
                    <p className="text-xs text-slate-400">{company.code}</p>
                  )}
                  {company.description && (
                    <p className="text-xs text-slate-400 truncate">
                      {company.description}
                    </p>
                  )}
                </div>
              </div>
            </DropdownMenuItem>
          ))
        )}
        
        {showManagementButton && authService.isAdmin() && (
          <>
            <DropdownMenuSeparator className="bg-slate-700" />
            <DropdownMenuItem
              onClick={() => {
                window.location.href = '/company-management';
              }}
              className="flex items-center gap-2 py-3 px-3 hover:bg-slate-700 cursor-pointer text-purple-400"
            >
              <Settings className="w-4 h-4" />
              <span>管理公司</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CompanySwitcher;
